import { useEffect, useState } from "react";
import { YoutubeLogo, ArrowSquareOut, Broadcast, GraduationCap } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { KajianFeed } from "@/components/KajianFeed";
import { LiteYouTube } from "@/components/LiteYouTube";

const ytId = (url) => { const m = (url || "").match(/(?:youtube\.com\/(?:live\/|watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/); return m ? m[1] : ""; };

const MUROTTAL = [
  { id: "tartil", name: "Tartil Al-Qur'an", url: "https://qurango.net/radio/tarateel" },
  { id: "maher", name: "Syaikh Maher", url: "https://qurango.net/radio/maher" },
  { id: "tafsir", name: "Tafsir Juz 'Amma", url: "https://qurango.net/radio/tafseer3ashr" },
];

export default function Radio() {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    api.get("/radio/links").then((r) => setLinks(r.data)).catch(() => {});
  }, []);

  const kajian = links.filter((l) => l.type === "kajian");
  const live = links.filter((l) => l.type === "live");
  const radio = links.filter((l) => l.type === "radio");

  return (
    <div>
      <PageHeader title="Kajian Parenting" />
      <div className="px-5 pt-5 pb-8">
        {/* SECTION 1: Kajian Parenting */}
        <div className="flex items-center gap-2 mb-3" data-testid="section-kajian">
          <GraduationCap size={22} weight="duotone" className="text-primary" />
          <h2 className="font-heading font-bold text-lg text-stone-800">Kajian Parenting</h2>
        </div>
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-3.5 mb-5" data-testid="kajian-intro">
          <p className="text-sm text-stone-700 leading-relaxed">
            Mohon Ayah Bunda sempatkan untuk mengikuti kajian parenting ini, bekali ilmu untuk mendampingi anak-anak kita di masa depan.
          </p>
          <p className="text-sm font-bold text-primary mt-1">Barakallahu fiikum</p>
        </div>
        <div className="space-y-4 mb-8">
          {kajian.length === 0 && <p className="text-stone-400 text-sm">Belum ada video kajian.</p>}
          {kajian.map((l) => (
            <div key={l.id} data-testid={`kajian-${l.id}`} className="bg-white rounded-2xl border border-stone-200/60 overflow-hidden shadow-sm">
              <div className="relative w-full aspect-video bg-stone-900">
                {ytId(l.url) ? (
                  <LiteYouTube id={ytId(l.url)} title={l.name} />
                ) : (
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center text-white gap-2">
                    <YoutubeLogo size={26} weight="fill" className="text-red-500" /> Buka video
                  </a>
                )}
              </div>
              <p className="font-semibold text-stone-800 text-sm px-3 pt-3 pb-2 line-clamp-2">{l.name}</p>
              <KajianFeed videoId={l.id} />
            </div>
          ))}
        </div>

        {/* SECTION 2: TV & Radio Dakwah */}
        <div className="flex items-center gap-2 mb-3" data-testid="section-tv-radio">
          <Broadcast size={22} weight="duotone" className="text-primary" />
          <h2 className="font-heading font-bold text-lg text-stone-800">TV &amp; Radio Dakwah</h2>
        </div>

        <p className="text-xs font-bold text-stone-500 uppercase tracking-wide mb-2">Live Streaming</p>
        <div className="space-y-2 mb-5">
          {live.length === 0 && <p className="text-stone-400 text-sm">Belum ada siaran live.</p>}
          {live.map((l) => (
            <div key={l.id} data-testid={`live-${l.id}`} className="bg-white rounded-2xl border border-stone-200/60 overflow-hidden shadow-sm">
              {ytId(l.url) && (
                <div className="relative w-full aspect-video bg-stone-900">
                  <LiteYouTube id={ytId(l.url)} title={l.name} />
                </div>
              )}
              <div className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center shrink-0"><YoutubeLogo size={20} weight="fill" className="text-red-600" /></div>
                <p className="flex-1 min-w-0 font-semibold text-stone-800 text-sm line-clamp-2">{l.name}</p>
                <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`live-link-${l.id}`}
                  className="shrink-0 bg-red-600 text-white font-bold text-xs px-3 py-2 rounded-full flex items-center gap-1 active:scale-95 transition-transform">
                  Tonton <ArrowSquareOut size={13} weight="bold" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs font-bold text-stone-500 uppercase tracking-wide mb-2">Radio Streaming</p>
        <div className="space-y-3">
          {/* Radio Murottal Al-Qur'an (audio streaming) */}
          <div className="bg-white rounded-2xl border border-stone-200/60 p-3.5 shadow-sm" data-testid="murottal-radio">
            <div className="flex items-center gap-3 mb-2.5">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0 text-lg">🎧</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-stone-800 text-sm">Radio Murottal Al-Qur'an</p>
                <p className="text-stone-400 text-xs">Streaming murottal langsung dari halaman</p>
              </div>
            </div>
            <div className="space-y-2">
              {MUROTTAL.map((m) => (
                <div key={m.id} className="flex items-center gap-2 bg-stone-50 rounded-xl p-2">
                  <span className="text-[11px] font-semibold text-stone-600 shrink-0 w-24 leading-tight">{m.name}</span>
                  <audio data-testid={`murottal-audio-${m.id}`} controls preload="none" src={m.url} className="flex-1 h-9 min-w-0" />
                </div>
              ))}
            </div>
          </div>

          {/* Radio Rodja (link, tidak bisa di-embed) */}
          {radio.map((l) => (
            <div key={l.id} data-testid={`radio-${l.id}`} className="bg-white rounded-2xl border border-stone-200/60 p-3.5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 text-lg">📻</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-stone-800 text-sm line-clamp-1">{l.name}</p>
                  {l.desc && <p className="text-stone-400 text-xs line-clamp-1">{l.desc}</p>}
                </div>
                <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`radio-link-${l.id}`}
                  className="shrink-0 bg-primary text-white font-bold text-xs px-3 py-2 rounded-full flex items-center gap-1 active:scale-95 transition-transform">
                  Dengarkan {l.name} <ArrowSquareOut size={13} weight="bold" />
                </a>
              </div>
              <p className="text-stone-400 text-[11px] mt-2">Gunakan app Radio Rodja untuk pengalaman terbaik</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
